/**
 * 获取推荐的运动类目应用场景类型
 */
export enum EventStatus {
  /**
   * Outright - 冠军赛事场景
   */
  OUTRIGHT = 5,

  /**
   * Closed - 已关闭赛事场景
   */
  CLOSED = 4,

  /**
   * Ended - 已结束赛事场景
   */
  ENDED = 3,

  /**
   * Live Events - 直播赛事场景
   */
  LIVE_EVENTS = 1,

  /**
   * Upcoming - 即将开始赛事场景
   */
  UPCOMING = 0,
}

/**
 * 获取推荐的运动类目应用场景类型
 * https://docs.sportradar.com/uof/api-and-structure/api/elements/less-than-sport_event_status-greater-than
 */
export enum EventStatusName {
  /**
   * Closed - 已关闭赛事场景
   */
  CLOSED = 'closed',

  /**
   * Ended - 已结束赛事场景
   */
  ENDED = 'ended',

  /**
   * Live Events - 直播赛事场景
   */
  LIVE_EVENTS = 'live',

  /**
   * Upcoming - 即将开始赛事场景
   */
  UPCOMING = 'not_started',
}

/**
 * 足球比赛状态码说明：
 *
 *  常规状态：
 *    0   - Not started（未开始）：比赛尚未开始
 *    61  - Start delayed（开始延迟）：比赛开始时间延迟，常规比赛时间
 *    6   - 1st half（上半场）：比赛进行中，上半场阶段
 *    7   - 2nd half（下半场）：比赛进行中，下半场阶段
 *    31  - Halftime（中场休息）：上下半场之间的休息时间
 *    32  - Awaiting extra time（等待加时赛）：常规时间结束，等待进入加时赛
 *    40  - Overtime（加时赛30分钟）
 *    41  - 1st extra（加时赛上半场）：加时赛第一个15分钟
 *    42  - 2nd extra（加时赛下半场）：加时赛第二个15分钟
 *    33  - Extra time halftime（加时赛中场休息）：加时赛上下半场之间的休息
 *    34  - Awaiting penalties（等待点球大战）：加时赛结束，等待进入点球大战
 *    50  - Penalties（点球大战）：正在进行点球大战
 *    100 - Ended（结束）：比赛正常结束（90分钟内结束）
 *    110 - AET（加时赛后结束）：After Extra Time，加时赛后结束
 *    120 - AP（点球大战后结束）：After Penalties，点球大战后结束
 *
 *  异常状态：
 *    60  - Postponed（推迟）：比赛被推迟到另一个日期
 *    70  - Cancelled（取消）：比赛被完全取消
 *    80  - Interrupted（中断）：赛事因各种原因中断（天气、安全等）
 *    90  - Abandoned（放弃）：比赛被永久终止，无法完成
 *
 *  不传时，默认包括所有状态
 */
export enum SoccerStatus {
  /**
   * 未开始 - 比赛尚未开始
   */
  NOT_STARTED = 0,

  /**
   * 开始延迟 - 比赛开始时间延迟 常规比赛时间
   */
  START_DELAYED = 61,

  /**
   * 上半场 - 比赛进行中，上半场阶段
   */
  FIRST_HALF = 6,

  /**
   * 下半场 - 比赛进行中，下半场阶段
   */
  SECOND_HALF = 7,

  /**
   * 中场休息 - 上下半场之间的休息时间 加时赛相关
   */
  HALFTIME = 31,

  /**
   * 等待加时赛 - 常规时间结束，等待进入加时赛
   */
  AWAITING_EXTRA_TIME = 32,

  /**
   * 加时赛 - 加时赛 30分钟
   */
  OVERTIME = 40,

  /**
   * 加时赛上半场 - 加时赛第一个15分钟
   */
  FIRST_EXTRA = 41,

  /**
   * 加时赛下半场 - 加时赛第二个15分钟
   */
  SECOND_EXTRA = 42,

  /**
   * 加时赛中场休息 - 加时赛上下半场之间的休息 点球大战
   */
  EXTRA_TIME_HALFTIME = 33,

  /**
   * 等待点球大战 - 加时赛结束，等待进入点球大战
   */
  AWAITING_PENALTIES = 34,

  /**
   * 点球大战 - 正在进行点球大战 比赛结束状态
   */
  PENALTIES = 50,

  /**
   * 结束 - 比赛正常结束（90分钟内结束）
   */
  ENDED = 100,

  /**
   * 加时赛后结束 - After Extra Time，加时赛后结束
   */
  AET = 110,

  /**
   * 点球大战后结束 - After Penalties，点球大战后结束
   */
  AP = 120,

  /**
   * 推迟 - 比赛被推迟到另一个日期
   */
  POSTPONED = 60,

  /**
   * 取消 - 比赛被完全取消
   */
  CANCELLED = 70,

  /**
   * 中断 - 赛因各种原因中断（天气、安全等）
   */
  INTERRUPTED = 80,

  /**
   * 放弃 - 比赛被永久终止，无法完成
   */
  ABANDONED = 90,
}

export enum AllSportStatus {
  // Duplicate names in the source sheet are suffixed with their numeric id.
  NOT_STARTED = 0,
  _1ST_PERIOD = 1,
  _2ND_PERIOD = 2,
  _3RD_PERIOD = 3,
  _4TH_PERIOD = 4,
  _5TH_PERIOD = 5,
  _1ST_HALF = 6,
  _2ND_HALF = 7,
  _1ST_SET = 8,
  _2ND_SET = 9,
  _3RD_SET = 10,
  _4TH_SET = 11,
  _5TH_SET = 12,
  _1ST_QUARTER_13 = 13,
  _2ND_QUARTER_14 = 14,
  _3RD_QUARTER_15 = 15,
  _4TH_QUARTER_16 = 16,
  GOLDEN_SET = 17,
  GOLDEN_RAID_18 = 18,
  COIN_TOSS = 19,
  STARTED = 20,
  IN_PROGRESS = 21,
  ABOUT_TO_START = 22,
  BREAK_30 = 30,
  HALFTIME = 31,
  AWAITING_EXTRA_TIME = 32,
  EXTRA_TIME_HALFTIME = 33,
  AWAITING_PENALTIES_34 = 34,
  AWAITING_PENALTIES_35 = 35,
  AWAITING_PENALTIES_36 = 36,
  AWAITING_GOLDEN_SET = 37,
  AWAITING_GOLDEN_RAID = 38,
  GOLDEN_RAID_39 = 39,
  OVERTIME_40 = 40,
  _1ST_EXTRA = 41,
  _2ND_EXTRA = 42,
  PENALTIES_50 = 50,
  PENALTIES_51 = 51,
  PENALTIES_52 = 52,
  PENALTIES_53 = 53,
  POSTPONED = 60,
  START_DELAYED = 61,
  CANCELLED = 70,
  GAME_1 = 71,
  GAME_2 = 72,
  GAME_3 = 73,
  GAME_4 = 74,
  GAME_5 = 75,
  GAME_6 = 76,
  GAME_7 = 77,
  INTERRUPTED = 80,
  SUSPENDED = 81,
  ABANDONED = 90,
  WALKOVER = 91,
  RETIRED = 92,
  WALKOVER_PLAYER_1_WON = 93,
  WALKOVER_PLAYER_2_WON = 94,
  PLAYER_1_RETIRED_PLAYER_2_WON = 95,
  PLAYER_2_RETIRED_PLAYER_1_WON = 96,
  PLAYER_1_DEFAULTED_PLAYER_2_WON = 97,
  PLAYER_2_DEFAULTED_PLAYER_1_WON = 98,
  ONLY_RESULT = 99,
  ENDED = 100,
  AET = 110,
  AOT = 111,
  AP_120 = 120,
  AP_121 = 121,
  OP_122 = 122,
  AP_123 = 123,
  OP_124 = 124,
  AP_125 = 125,
  AFTER_GOLDEN_SET = 130,
  AFTER_GOLDEN_RAID = 140,
  _1ST_MAP = 141,
  _2ND_MAP = 142,
  _3RD_MAP = 143,
  _4TH_MAP = 144,
  _5TH_MAP = 145,
  _6TH_MAP = 146,
  _7TH_MAP = 147,
  _1ST_GAME = 151,
  _2ND_GAME = 152,
  _3RD_GAME = 153,
  _4TH_GAME = 154,
  _5TH_GAME = 155,
  _1ST_END = 161,
  _2ND_END = 162,
  _3RD_END = 163,
  _4TH_END = 164,
  _5TH_END = 165,
  _6TH_END = 166,
  _7TH_END = 167,
  _8TH_END = 168,
  _9TH_END = 169,
  _10TH_END = 170,
  EXTRA_END = 171,
  CURRENT = 200,
  PERIOD_1 = 201,
  PERIOD_2 = 202,
  PERIOD_3 = 203,
  PERIOD_4 = 204,
  PERIOD_5 = 205,
  NORMAL_TIME = 206,
  EXTRA_PERIOD_1 = 207,
  EXTRA_PERIOD_2 = 208,
  OVERTIME_209 = 209,
  PENALTIES_210 = 210,
  AGGREGATED = 211,
  FIRST_HALF = 212,
  SECOND_HALF = 213,
  FIRST_EXTRA_HALF = 214,
  SECOND_EXTRA_HALF = 215,
  FIRST_EXTRA_PERIOD = 216,
  SECOND_EXTRA_PERIOD = 217,
  THIRD_EXTRA_PERIOD = 218,
  FOURTH_EXTRA_PERIOD = 219,
  FIFTH_EXTRA_PERIOD = 220,
  SET_1 = 221,
  SET_2 = 222,
  SET_3 = 223,
  SET_4 = 224,
  SET_5 = 225,
  _1ST_QUARTER_226 = 226,
  _2ND_QUARTER_227 = 227,
  _3RD_QUARTER_228 = 228,
  _4TH_QUARTER_229 = 229,
  _1ST_THIRD = 230,
  _2ND_THIRD = 231,
  _3RD_THIRD = 232,
  PERIOD_6 = 233,
  PERIOD_7 = 234,
  PERIOD_8 = 235,
  PERIOD_9 = 236,
  PERIOD_10 = 237,
  PERIOD_11 = 238,
  PERIOD_12 = 239,
  PERIOD_13 = 240,
  FIRST_BREAK = 301,
  SECOND_BREAK = 302,
  THIRD_BREAK = 303,
  FOURTH_BREAK = 304,
  FIFTH_BREAK = 305,
  SIXTH_BREAK = 306,
  _1ST_INNING_TOP = 401,
  _1ST_INNING_BOTTOM = 402,
  _2ND_INNING_TOP = 403,
  _2ND_INNING_BOTTOM = 404,
  _3RD_INNING_TOP = 405,
  _3RD_INNING_BOTTOM = 406,
  _4TH_INNING_TOP = 407,
  _4TH_INNING_BOTTOM = 408,
  _5TH_INNING_TOP = 409,
  _5TH_INNING_BOTTOM = 410,
  _6TH_INNING_TOP = 411,
  _6TH_INNING_BOTTOM = 412,
  _7TH_INNING_TOP = 413,
  _7TH_INNING_BOTTOM = 414,
  _8TH_INNING_TOP = 415,
  _8TH_INNING_BOTTOM = 416,
  _9TH_INNING_TOP = 417,
  _9TH_INNING_BOTTOM = 418,
  EXTRA_INNING_TOP = 419,
  EXTRA_INNING_BOTTOM = 420,
  BREAK_TOP_1_BOTTOM_1 = 421,
  BREAK_TOP_2_BOTTOM_1 = 422,
  BREAK_TOP_2_BOTTOM_2 = 423,
  BREAK_TOP_3_BOTTOM_2 = 424,
  BREAK_TOP_3_BOTTOM_3 = 425,
  BREAK_TOP_4_BOTTOM_3 = 426,
  BREAK_TOP_4_BOTTOM_4 = 427,
  BREAK_TOP_5_BOTTOM_4 = 428,
  BREAK_TOP_5_BOTTOM_5 = 429,
  BREAK_TOP_6_BOTTOM_5 = 430,
  BREAK_TOP_6_BOTTOM_6 = 431,
  BREAK_TOP_7_BOTTOM_6 = 432,
  BREAK_TOP_7_BOTTOM_7 = 433,
  BREAK_TOP_8_BOTTOM_7 = 434,
  BREAK_TOP_8_BOTTOM_8 = 435,
  BREAK_TOP_9_BOTTOM_8 = 436,
  BREAK_TOP_9_BOTTOM_9 = 437,
  BREAK_TOP_EI_BOTTOM_9 = 438,
  BREAK_TOP_EI_BOTTOM_EI = 439,
  SUDDEN_DEATH = 440,
  _6TH_SET = 441,
  _7TH_SET = 442,
  AWAITING_SUDDEN_DEATH = 443,
  AFTER_SUDDEN_DEATH = 444,
  BREAK_445 = 445,
  FIRST_INNINGS_HOME_TEAM = 501,
  FIRST_INNINGS_AWAY_TEAM = 502,
  SECOND_INNINGS_HOME_TEAM = 503,
  SECOND_INNINGS_AWAY_TEAM = 504,
  AWAITING_SUPER_OVER = 505,
  SUPER_OVER_HOME_TEAM = 506,
  SUPER_OVER_AWAY_TEAM = 507,
  AFTER_SUPER_OVER = 508,
  INNINGS_BREAK = 509,
  SUPER_OVER_BREAK = 510,
  LUNCH_BREAK = 511,
  TEA_BREAK = 512,
  STUMPS = 513,
  _8TH_SET = 514,
  _9TH_SET = 515,
  _10TH_SET = 516,
  _11TH_SET = 517,
  _12TH_SET = 518,
  _13TH_SET = 519,
  THIRD_INNINGS_HOME_TEAM = 520,
  THIRD_INNINGS_AWAY_TEAM = 521,
  FOURTH_INNINGS_HOME_TEAM = 522,
  FOURTH_INNINGS_AWAY_TEAM = 523,
  DINNER_BREAK = 524,
  DRINKS = 525,
  SUPER_OVER = 526,
  _1ST_INNING = 531,
  _2ND_INNING = 532,
  _3RD_INNING = 533,
  _4TH_INNING = 534,
  _5TH_INNING = 535,
  _6TH_INNING = 536,
  _7TH_INNING = 537,
  _8TH_INNING = 538,
  _9TH_INNING = 539,
  FIRST_ROUND = 540,
  SECOND_ROUND = 541,
  THIRD_ROUND = 542,
  FOURTH_ROUND = 543,
  FIFTH_ROUND = 544,
  SIXTH_ROUND = 545,
  SEVENTH_ROUND = 546,
  BREAK_TOP_EI_BOTTOM_3 = 547,
  BREAK_TOP_EI_BOTTOM_7 = 548,
}

// Big State
export enum AllSportMatchStatus {
  not_started = 0,
  live = 1,
  suspended = 2,
  ended = 3,
  closed = 4,
  cancelled = 5,
  abandoned = 9,
  delayed = 6,
  unknown = 10,
  postponed = 8,
  interrupted = 7,
}
